import { dialog, InfiniteScroll } from "../../../nq-component";
import BaseListPage from "../../../base/BaseListPage.js";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  updateObjectUseCase,
  upsertUseCase,
} from "../../../usecases/object/index.js";
import { getCurrentUserUseCase } from "../../../usecases/user/index.js";
import withRouter from "../../../withRouter.js";
import ReceiptListPagePresenter from "./ReceiptsListPagePresenter.js";
import DialogCreate from "../../../components/DialogCreate/index.js";
import AddButton from "../../../components/AddButton/index.js";
import CustomLoader from "../../../components/CustomLoader/index.js";
import FormDialog from "./components/FormDialog/index.js";
import PageHeader from "../../../components/PageHeader/index.js";
import ReceiptsTable from "./components/ReceiptsTable.js";
import DialogOptions from "./components/DialogOptions.js";
import CollapseDetails from "./components/CollapseDetails.js";
import CustomConfirmation from "../../../components/CustomConfirmation/index.js";

class ReceiptListPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.state = {
      selected: [],
      objects: [],
      userObject: null,
    };

    this.presenter = new ReceiptListPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
      getCurrentUserUseCase(),
      updateObjectUseCase(),
    );
  }

  getCollectionName() {
    return "purchases";
  }

  getParamsMonth() {
    return this.props.params.month;
  }

  getParamsYear() {
    return this.props.params.year;
  }

  getBaseRoute() {
    return `/purchases/${this.getParamsMonth()}/${this.getParamsYear()}`;
  }

  getMonths() {
    return this.presenter.getMonths();
  }

  getClientId() {
    return this.props.params.clientId;
  }

  onSubmitModal(collection, object) {
    this.presenter.onSubmitModal(collection, object);
  }

  createModal(collection, field, placeholder) {
    dialog.fire({
      html: (
        <DialogCreate
          placeholder={placeholder}
          field={field}
          collection={collection}
          onSubmitModal={this.onSubmitModal.bind(this)}
        />
      ),
      footer: false,
    });
  }

  onDelete(id) {
    this.presenter.onDelete(id);
  }

  onDeleteModal(objectId) {
    dialog.fire({
      html: <CustomConfirmation onConfirm={() => this.onDelete(objectId)} />,
      footer: false,
    });
  }

  onCloseDialog() {
    dialog.close();
  }

  onFlexibleEntry() {
    const month = this.getParamsMonth();
    const year = this.getParamsYear();
    dialog.close();
    this.navigateTo(`/purchases/form/flexible_entry/${month}/${year}`);
  }

  batchByDate() {
    const month = this.getParamsMonth();
    const year = this.getParamsYear();
    dialog.close();
    this.navigateTo(`/purchases/form/by_date/${month}/${year}`);
  }

  groupepBySupplier() {
    const month = this.getParamsMonth();
    const year = this.getParamsYear();
    dialog.close();
    this.navigateTo(`/purchases/form/by_supplier/${month}/${year}`);
  }

  onSubmit(updatedObject, originalObject) {
    this.presenter.onSubmit(updatedObject, originalObject);
  }

  onEdit(object) {
    const { receipts, userObject } = this.state;
    const schemas = this.getSchemas();
    const schema = this.getSchema(this.getCollectionName());
    const isAdmin = this.isAdmin();
    const clientId = this.getClientId();

    const month = this.getParamsMonth();
    const year = this.getParamsYear();
    const paddedMonth = String(month).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();

    const minDate = `${year}-${paddedMonth}-01`;
    const maxDate = `${year}-${paddedMonth}-${String(lastDay).padStart(
      2,
      "0",
    )}`;

    const user = this.getCurrentUser();
    dialog.fire({
      html: (
        <FormDialog
          onSubmit={this.onSubmit.bind(this)}
          onCancelRequested={this.onDiscardConfirm.bind(this)}
          object={object}
          receipts={receipts}
          setState={this.setState.bind(this)}
          schema={schema}
          schemas={schemas}
          minDate={minDate}
          maxDate={maxDate}
          user={user}
          clientId={clientId || user?.client?.id}
          disabled={
            object?.status === "audited" ||
            (object?.status === "pending" && isAdmin) ||
            (object?.status === "for_audit" && !isAdmin)
          }
        />
      ),
      footer: false,
    });
  }

  onDiscardConfirm() {
    const message =
      "You have unsaved changes that will be lost if you proceed. Are you sure you want to discard these changes?";
    this.showConfirmDialog(message, "Discard Changes", "DISCARD", "Cancel")
      .then(() => dialog.close())
      .catch(() => {});
  }

  onClickAdd() {
    dialog.fire({
      html: (
        <DialogOptions
          onClickOption1={this.onFlexibleEntry.bind(this)}
          onClickOption2={this.batchByDate.bind(this)}
          onClickOption3={this.groupepBySupplier.bind(this)}
        />
      ),
      footer: false,
    });
  }

  onConfirmAudit(object = null) {
    const { selected } = this.state;
    const itemsToAudit = object?.id ? [object] : selected;

    if (!itemsToAudit || itemsToAudit.length === 0) {
      console.warn("No items selected for audit");
      return;
    }

    const message =
      itemsToAudit.length === 1
        ? "By confirming, this indicates that all entered details and supporting documents have been fully reviewed and verified as correct."
        : `By confirming, this indicates that all entered details and supporting documents for ${itemsToAudit.length} items have been fully reviewed and verified as correct.`;

    this.showConfirmDialog(message, "Confirm Audit Review", "Confirm", "Cancel")
      .then(() => {
        const dataToAudit =
          itemsToAudit.length === 1 ? itemsToAudit[0] : itemsToAudit;
        this.presenter.purchasesAudited(dataToAudit);
      })
      .catch(() => {});
  }

  onCollapse(index, object) {
    const isAdmin = this.isAdmin();
    return (
      <CollapseDetails
        object={object}
        onDelete={this.onDeleteModal.bind(this)}
        onConfirm={this.onConfirmAudit.bind(this)}
        onEdit={this.onEdit.bind(this)}
        isAdmin={isAdmin}
      />
    );
  }

  selectedAction() {
    const { selected } = this.state;
    const isAdmin = this.isAdmin();

    if (selected && selected.length !== 0 && isAdmin) {
      return (
        <button
          className="btn text-center text-white bg-success fw-medium"
          onClick={this.onConfirmAudit.bind(this)}
        >
          <i className="bi bi-check2 me-2"></i>
          Confirm Audit
        </button>
      );
    }
    return null;
  }

  render() {
    const schema = this.getSchema(this.getCollectionName());
    const schemas = this.getSchemas();
    const user = this.getCurrentUser();
    const clientId = this.getClientId();
    const isAdmin = this.isAdmin();

    if (!schema) return <CustomLoader />;
    const { objects, progress, count, isMonthsVisible, selected } = this.state;

    const intMonth = this.getParamsMonth();
    const month = this.getMonths()[intMonth - 1];

    const forAudit = objects.some(
      (object) =>
        object?.status === "for_audit" || object?.status === "audited",
    );
    return (
      <>
        <div className="overflow-auto">
          <InfiniteScroll
            className="h-100"
            loadMore={this.loadMore.bind(this)}
            hasMore={!progress && count > objects.length}
          >
            <div className="p-3 p-lg-4">
              <div className="d-flex flex-column gap-4">
                <PageHeader
                  breadcrumb={[
                    { label: "Home", path: "/" },
                    { label: "Purchases", path: "/purchases" },
                    { label: `${month} Purchases` },
                  ]}
                  selected={selected}
                  objects={objects}
                  count={count}
                  title={`${month} Purchases`}
                  backButton
                  action={this.selectedAction.bind(this)}
                />

                {/* Table Card */}

                {progress && objects.length === 0 ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px", padding: "3rem" }}
                  >
                    <CustomLoader />
                  </div>
                ) : (
                  <div
                    className="rounded-3 shadow-sm"
                    style={{
                      border: "1px solid #e9ecef",
                      overflow: "hidden",
                    }}
                  >
                    <ReceiptsTable
                      objects={objects}
                      schema={schema}
                      schemas={schemas}
                      selected={selected}
                      selectable={isAdmin ? true : false}
                      onSelect={this.onSelect.bind(this)}
                      onSelectAll={this.onSelectAll.bind(this)}
                      onSort={this.onClickSort.bind(this)}
                      onEdit={this.onEdit.bind(this)}
                      progress={progress}
                      unselectable={{ status: "audited" }}
                      actions={[
                        {
                          label: (object) => {
                            if (isAdmin) {
                              return object?.status !== "audited"
                                ? "Edit"
                                : "View";
                            }
                            return object?.status === "for_audit" ||
                              object?.status === "audited" ||
                              object?.status === "non-vat"
                              ? "View"
                              : "Edit";
                          },
                          icon: (object) => {
                            if (isAdmin) {
                              return object?.status !== "audited"
                                ? "bi bi-pencil"
                                : "bi bi-eye";
                            }
                            return object?.status === "for_audit" ||
                              object?.status === "audited" ||
                              object?.status === "non-vat"
                              ? "bi bi-eye"
                              : "bi bi-pencil";
                          },
                          onClick: this.onEdit.bind(this),
                        },
                        ...(isAdmin
                          ? [
                              {
                                label: "Confirm Audit",
                                icon: "bi bi-check-circle",
                                show: (object) => object.status === "for_audit",
                                className: "btn btn-sm bg-success",
                                onClick: this.onConfirmAudit.bind(this),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>

            <AddButton
              onClick={this.onClickAdd.bind(this)}
              show={!isAdmin && !forAudit}
            />
          </InfiniteScroll>
        </div>
      </>
    );
  }
}

export default withRouter(ReceiptListPage);
